import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import { formatMoney, formatDate, titleCase } from '../utils/format';
import StatusBadge from '../components/StatusBadge';
import PageHeader from '../components/PageHeader';

export default function AuctionDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    api.get(`/auctions/${id}`)
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load auction'));
  }, [id]);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return <div className="page-loading">Loading…</div>;

  const { auction, bids, transactions } = data;

  return (
    <div>
      <Link to="/auctions" className="back-link">← Back to auctions</Link>
      <PageHeader
        title={auction.title}
        subtitle={[auction.lot_number, auction.location].filter(Boolean).join(' · ') || undefined}
      />

      <div className="detail-grid">
        <div className="detail-item"><span>Status</span><StatusBadge value={auction.status} /></div>
        <div className="detail-item"><span>Approval</span><StatusBadge value={auction.approval_stage} /></div>
        <div className="detail-item"><span>Lot #</span><strong>{auction.lot_number || '—'}</strong></div>
        <div className="detail-item"><span>Serial #</span><strong>{auction.serial_number || '—'}</strong></div>
        <div className="detail-item"><span>Location</span><strong>{auction.location || '—'}</strong></div>
        <div className="detail-item"><span>Starting Price</span><strong>{formatMoney(auction.starting_price)}</strong></div>
        <div className="detail-item"><span>Current Price</span><strong>{formatMoney(auction.current_price)}</strong></div>
        <div className="detail-item"><span>Reserve Price</span><strong>{formatMoney(auction.reserve_price)}</strong></div>
        <div className="detail-item"><span>Starts</span><strong>{formatDate(auction.start_time)}</strong></div>
        <div className="detail-item"><span>Ends</span><strong>{formatDate(auction.end_time)}</strong></div>
        <div className="detail-item"><span>Winner</span><strong>{auction.winner_username || '—'}</strong></div>
        <div className="detail-item"><span>Created By</span><strong>{auction.created_by_username || '—'}</strong></div>
      </div>

      {auction.description && (
        <div className="panel">
          <div className="panel-header"><h2>Description</h2></div>
          <p className="description-text">{auction.description}</p>
        </div>
      )}

      <div className="panel-grid">
        <div className="panel">
          <div className="panel-header"><h2>Bids ({bids.length})</h2></div>
          <table className="data-table">
            <thead>
              <tr><th>Bidder</th><th>Amount</th><th>Deposit</th><th>Winning</th><th>Placed</th></tr>
            </thead>
            <tbody>
              {bids.length === 0 && <tr><td colSpan="5" className="empty-cell">No bids yet</td></tr>}
              {bids.map((b) => (
                <tr key={b.id}>
                  <td>{b.username}</td>
                  <td>{formatMoney(b.bid_amount)}</td>
                  <td>{b.deposit_paid ? formatMoney(b.deposit_amount) : 'Unpaid'}</td>
                  <td>{b.is_winning ? 'Yes' : 'No'}</td>
                  <td>{formatDate(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="panel">
          <div className="panel-header"><h2>Transactions ({transactions.length})</h2></div>
          <table className="data-table">
            <thead>
              <tr><th>User</th><th>Type</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {transactions.length === 0 && <tr><td colSpan="4" className="empty-cell">No transactions</td></tr>}
              {transactions.map((t) => (
                <tr key={t.id}>
                  <td>{t.username}</td>
                  <td>{titleCase(t.transaction_type)}</td>
                  <td>{formatMoney(t.amount)}</td>
                  <td><StatusBadge value={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
